/** True when the API returned a non-answer (parse failure, NOT_FOUND, errors). */
export const isUnusableAiAnswer = (answer) => {
  if (answer == null || typeof answer !== "string") return true;
  const text = answer.trim();
  if (!text) return true;
  const lower = text.toLowerCase();
  return (
    lower === "could not parse answer from response." ||
    lower.startsWith("not_found:") ||
    lower === "no relevant content found for this company." ||
    lower === "error during query." ||
    lower === "error during rag query."
  );
};

/** Strip markdown for plain-text textarea display only. */
export const stripMarkdown = (text) => {
  if (typeof text !== "string") return text ?? "";
  return text
    .replace(/\*\*([^*]+)\*\*/g, "$1")
    .replace(/(?<![_\w])__([^_]+)__(?![_\w])/g, "$1")
    .replace(/\*([^*\n]+)\*/g, "$1")
    .replace(/(?<![_\w])_([^_\n]+)_(?![_\w])/g, "$1")
    .replace(/^#{1,6}\s+/gm, "")
    .replace(/^\s*[-*+•]\s+/gm, "")
    .trim();
};

export const looksLikeMarkdown = (text) => {
  if (typeof text !== "string" || !text.trim()) return false;
  return (
    /\*\*[^*]+\*\*/.test(text) ||
    /^\s*[-*+•]\s+/m.test(text) ||
    /^\s*\d+\.\s+/m.test(text) ||
    /^#{1,6}\s+/m.test(text)
  );
};

export const parseInlineSegments = (text) => {
  const input = String(text ?? "");
  const segments = [];
  const re = /\*\*([^*]+)\*\*/g;
  let last = 0;
  let match;

  while ((match = re.exec(input)) !== null) {
    if (match.index > last) {
      segments.push({ text: input.slice(last, match.index), bold: false });
    }
    segments.push({ text: match[1], bold: true });
    last = re.lastIndex;
  }

  if (last < input.length) {
    segments.push({ text: input.slice(last), bold: false });
  }

  if (!segments.length) {
    segments.push({ text: input, bold: false });
  }

  return segments;
};

export const parseMarkdownBlocks = (text) => {
  const lines = String(text ?? "").replace(/\r\n/g, "\n").split("\n");
  const blocks = [];
  let currentList = null;

  const flushList = () => {
    if (currentList) {
      blocks.push(currentList);
      currentList = null;
    }
  };

  for (const line of lines) {
    const trimmed = line.trim();
    if (!trimmed) {
      flushList();
      continue;
    }

    const bulletMatch = trimmed.match(/^[-*+•]\s+(.*)$/);
    const orderedMatch = trimmed.match(/^\d+\.\s+(.*)$/);

    if (bulletMatch) {
      if (!currentList || currentList.type !== "ul") {
        flushList();
        currentList = { type: "ul", items: [] };
      }
      currentList.items.push(bulletMatch[1]);
    } else if (orderedMatch) {
      if (!currentList || currentList.type !== "ol") {
        flushList();
        currentList = { type: "ol", items: [] };
      }
      currentList.items.push(orderedMatch[1]);
    } else {
      flushList();
      blocks.push({ type: "p", text: trimmed.replace(/^#{1,6}\s+/, "") });
    }
  }

  flushList();
  return blocks;
};

const FORMATTED_ANSWERS_KEY = "answersFormatted";

export const loadAnswersFormatted = () => JSON.parse(localStorage.getItem(FORMATTED_ANSWERS_KEY) || "{}");

export const saveAnswersFormatted = (data) => {
  localStorage.setItem(FORMATTED_ANSWERS_KEY, JSON.stringify(data));
};

export const S1_6_COLUMN_HEADERS_KEY = "s1_6_columnHeaders";

export const S1_6_ROW_DEFS = [
  { key: "totalEmp", label: "Total no. of employees" },
  { key: "permanent", label: "No. of permanent employees" },
  { key: "temporary", label: "No. of temporary employees" },
  { key: "nonGuaranteedHours", label: "No. of non-guaranteed hours employees" },
  { key: "fullTime", label: "No. of full-time employees" },
  { key: "partTime", label: "No. of part-time employees" },
];

const escapeHtml = (value) =>
  String(value ?? "")
    .replace(/&/g, "&amp;")
    .replace(/</g, "&lt;")
    .replace(/>/g, "&gt;");

/** Resolve country columns for S1-6 from persisted headers or tableAnswers keys. Regions are intentionally excluded — the S1-6 table only shows countries. */
export function resolveS1_6ColumnHeaders(tableAnswers, storedHeaders) {
  const countries = Array.isArray(storedHeaders?.countries) ? storedHeaders.countries.filter(Boolean) : [];
  if (countries.length > 0) {
    return { countries, regions: [] };
  }
  try {
    const s1 = JSON.parse(localStorage.getItem("s1") || "{}");
    const s1_6 = s1.S1_6_Summary;
    if (s1_6) {
      const fromApiCountries = Object.keys(s1_6.countryEmployeeCountForTotal || {}).filter(Boolean);
      if (fromApiCountries.length > 0) {
        return { countries: fromApiCountries, regions: [] };
      }
    }
  } catch {
    /* ignore */
  }
  const ta = tableAnswers || {};
  const inferred = [];
  Object.keys(ta).forEach((key) => {
    const match = key.match(/^s1_6-totalEmp-(.+)$/);
    if (match && match[1] !== "Total") inferred.push(match[1]);
  });
  return { countries: inferred, regions: [] };
}

/** Build full S1-6 HTML for preview/PDF from tableAnswers (country columns only). */
export function buildS1_6TableHtmlFromAnswers(tableAnswers, storedHeaders) {
  const ta = tableAnswers || {};
  const { countries } = resolveS1_6ColumnHeaders(ta, storedHeaders);
  const colSpan = 2 + countries.length;

  let html = "<table><thead>";
  html += `<tr><th colspan="${colSpan}">Table: S1-6 – Characteristics of the Undertaking’s Employees - by country</th></tr>`;
  html += "<tr><th>Information on employees by gender</th>";
  countries.forEach((c) => {
    html += `<th>${escapeHtml(c)}</th>`;
  });
  html += "<th>Total</th></tr></thead><tbody>";

  S1_6_ROW_DEFS.forEach(({ key, label }) => {
    html += "<tr>";
    html += `<th>${escapeHtml(label)}</th>`;
    countries.forEach((c) => {
      const val = ta[`s1_6-${key}-${c}`];
      html += `<td>${escapeHtml(val != null && val !== "" ? val : "")}</td>`;
    });
    const totalVal = ta[`s1_6-${key}-Total`];
    html += `<td>${escapeHtml(totalVal != null && totalVal !== "" ? totalVal : "")}</td>`;
    html += "</tr>";
  });

  html += "</tbody></table>";
  return html;
}

export function loadS1_6ColumnHeaders() {
  try {
    return JSON.parse(localStorage.getItem(S1_6_COLUMN_HEADERS_KEY) || "{}");
  } catch {
    return { countries: [], regions: [] };
  }
}

export function saveS1_6ColumnHeaders(countries, regions) {
  localStorage.setItem(S1_6_COLUMN_HEADERS_KEY, JSON.stringify({ countries, regions }));
}

const isS1_6Step4Item = (item) => item?.id === "q-756";

/** Prefer live tableAnswers for S1-6 so preview always includes country/region columns. */
export function resolveS1_6PdfAnswer(item, fallbackAnswer) {
  if (!isS1_6Step4Item(item)) return fallbackAnswer;
  try {
    const ta = JSON.parse(localStorage.getItem("tableAnswers") || "{}");
    if (!Object.keys(ta).some((k) => k.startsWith("s1_6-"))) return fallbackAnswer;
    return buildS1_6TableHtmlFromAnswers(ta, loadS1_6ColumnHeaders());
  } catch {
    return fallbackAnswer;
  }
}

/** Saved table answers are HTML strings in step4.answer (not in localStorage answers). */
export const isTableAnswer = (answer) => typeof answer === "string" && /<table[\s>]/i.test(answer);

/** Build PDF report rows from step4 metadata + saved answers; uses formatted markdown when available. */
export const loadReportDataForPdf = () => {
  const step4 = JSON.parse(localStorage.getItem("step4") || "[]");
  const savedAnswers = JSON.parse(localStorage.getItem("answers") || "{}");
  const answersFormatted = loadAnswersFormatted();

  const byId = new Map();
  const byQuestion = new Map();
  step4.forEach((item) => {
    if (item?.id) byId.set(item.id, item);
    const q = typeof item?.question === "string" ? item.question.trim() : "";
    if (q) byQuestion.set(q, item);
  });

  const seen = new Set();
  const result = [];

  const pushItem = (item, answer, id) => {
    const resolved = resolveS1_6PdfAnswer(item, answer);
    const pdfAnswer = (id && answersFormatted[id]) || resolved;
    if (pdfAnswer == null || String(pdfAnswer).trim() === "") return;
    const key = item?.id ?? (typeof item?.question === "string" ? item.question.trim() : "");
    if (!key || seen.has(key)) return;
    seen.add(key);
    result.push({ ...item, answer: pdfAnswer });
  };

  // Text answers saved via handleSave (localStorage answers keyed by question id).
  Object.entries(savedAnswers).forEach(([id, answer]) => {
    const item = byId.get(id) || byQuestion.get(id);
    if (item) pushItem(item, answer, id);
  });

  // Table answers and legacy step4 rows: HTML lives on step4[].answer after handleTableSave.
  step4.forEach((item) => {
    const id = item?.id;
    const key = id ?? (typeof item?.question === "string" ? item.question.trim() : "");
    if (key && seen.has(key)) return;

    if (isTableAnswer(item?.answer) || isS1_6Step4Item(item)) {
      const tableHtml = resolveS1_6PdfAnswer(item, item?.answer);
      if (isTableAnswer(tableHtml)) {
        pushItem(item, tableHtml, id);
      }
      return;
    }

    if (!id) {
      pushItem(item, item?.answer);
    }
  });

  return result;
};
